import * as React from "react";
import {
	CreatePodWorkspaceModal,
	type KnowledgeLevel,
	type PodcastLanguage,
} from "../components/createPodWorkspaceModal.component";
import {
	PodWorkspaceService,
	type PodWorkspace,
} from "@/lib/podWorkspace/podWorkspace.service";
import { BaseMediumService } from "@/lib/baseMedium/baseMedium.service";
import { PodcastLength } from "@/lib/coin/coin.service";

type FlowSuccess = {
	workspace: PodWorkspace;
	knowledgeLevel: KnowledgeLevel;
	podcastLength: PodcastLength;
	language: PodcastLanguage;
};

type UseCreatePodWorkspaceFlowOptions = {
	onSuccess?: (data: FlowSuccess) => void;
	onError?: (error: unknown) => void;
	defaultKnowledgeLevel?: KnowledgeLevel;
	defaultPodcastLength?: PodcastLength;
	defaultLanguage?: PodcastLanguage;
	/**
	 * Called after the podcast was successfully created and the generation flow started.
	 * Use this to immediately refresh pod workspaces/podcasts lists.
	 */
	onPodcastCreated?: () => void;
	/**
	 * Called when the modal closes. Use this to refetch data in case anything changed
	 * while the modal was open (e.g., workspaces were deleted).
	 */
	onClose?: () => void;
};

export function useCreatePodWorkspaceFlow(
	options?: UseCreatePodWorkspaceFlowOptions,
) {
	const [open, setOpen] = React.useState(false);
	const [isProcessing, setIsProcessing] = React.useState(false);
	const [workspaceId, setWorkspaceId] = React.useState<string | undefined>(
		undefined,
	);
	const [initialFile, setInitialFile] = React.useState<File | undefined>(
		undefined,
	);
	const [initialStorageFileId, setInitialStorageFileId] = React.useState<
		string | undefined
	>(undefined);

	const openFlow = React.useCallback((file?: File, storageFileId?: string) => {
		setInitialFile(file);
		setInitialStorageFileId(storageFileId);
		setOpen(true);
	}, []);
	const closeFlow = React.useCallback(() => {
		setOpen(false);
		setWorkspaceId(undefined);
		setInitialFile(undefined);
		setInitialStorageFileId(undefined);
	}, []);

	const handleCreate = React.useCallback(
		async (payload: {
			blobLink: string;
			title: string;
			knowledgeLevel: KnowledgeLevel;
			podcastLength: PodcastLength;
			language: PodcastLanguage;
		}): Promise<PodWorkspace> => {
			setIsProcessing(true);
			try {
				// 1) Create Pod Workspace (use paper title for workspace title)
				const workspace = await PodWorkspaceService.createPodWorkspace({
					title: payload.title,
				});
				setWorkspaceId(workspace.id);

				// 2) Create BaseMedium from the already-extracted file
				// This will trigger transcription and create podcast with chapters
				await BaseMediumService.createFromExtracted(
					payload.blobLink,
					payload.title,
					workspace.id,
					payload.podcastLength,
					payload.language,
				);

				// Note: We don't call approve-cost here anymore
				// The modal will handle getting the podcast ID and calling approve-cost

				options?.onSuccess?.({
					workspace,
					knowledgeLevel: payload.knowledgeLevel,
					podcastLength: payload.podcastLength,
					language: payload.language,
				});

				return workspace;
			} catch (err) {
				options?.onError?.(err);
				throw err;
			} finally {
				setIsProcessing(false);
			}
		},
		[options],
	);

	const element = (
		<CreatePodWorkspaceModal
			open={open}
			onOpenChange={(v) => (v ? setOpen(true) : closeFlow())}
			onCreate={handleCreate}
			defaultKnowledgeLevel={options?.defaultKnowledgeLevel ?? "intermediate"}
			defaultPodcastLength={
				options?.defaultPodcastLength ?? PodcastLength.MEDIUM
			}
			workspaceId={workspaceId}
			onPodcastCreated={options?.onPodcastCreated}
			onClose={options?.onClose}
			initialFile={initialFile}
			initialStorageFileId={initialStorageFileId}
		/>
	);

	return {
		open: openFlow,
		close: closeFlow,
		element,
		isProcessing,
	};
}
